import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Hides home sections listed in site-config `hidden_sections`; in edit mode admins get a delete chip on each. */
export default function HomeSectionControls() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => {
    try { return JSON.parse(localStorage.getItem("home_hidden_sections") || "[]"); } catch { return []; }
  });
  const [targets, setTargets] = useState([]);

  useEffect(() => {
    api.get("/site-config").then((r) => {
      const h = r.data?.hidden_sections || [];
      setHidden(h);
      localStorage.setItem("home_hidden_sections", JSON.stringify(h));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    const scan = () => setTargets([...document.querySelectorAll("[data-home-section]")]);
    const timers = [300, 1500, 4000].map((ms) => setTimeout(scan, ms));
    return () => timers.forEach(clearTimeout);
  }, [user]);

  const save = async (next, msg) => {
    try {
      await api.put("/admin/site-config", { hidden_sections: next });
      setHidden(next);
      localStorage.setItem("home_hidden_sections", JSON.stringify(next));
      toast.success(msg);
    } catch {
      toast.error("Errore salvataggio");
    }
  };

  const remove = (el) => {
    const id = el.getAttribute("data-home-section");
    const label = el.getAttribute("data-home-label") || id;
    if (!window.confirm(`Eliminare la sezione "${label}" dalla home?`)) return;
    save([...new Set([...hidden, id])], "Sezione eliminata");
  };

  const css = hidden.map((id) => `[data-home-section="${id}"]{display:none!important}`).join("");

  return (
    <>
      {css && <style>{css}</style>}
      {user?.is_admin && targets.map((el) =>
        createPortal(
          <button
            type="button"
            data-testid={`delete-section-${el.getAttribute("data-home-section")}`}
            onClick={() => remove(el)}
            className="edit-mode-only absolute top-3 left-3 z-40 bg-red-600 hover:bg-red-700 text-white text-[10px] uppercase tracking-widest font-bold px-3 h-8 items-center gap-1.5 shadow-lg"
          >
            <Trash2 className="w-3 h-3" /> Elimina sezione
          </button>,
          el,
        ),
      )}
      {user?.is_admin && hidden.length > 0 && (
        <button
          type="button"
          data-testid="restore-hidden-sections"
          onClick={() => save([], "Sezioni ripristinate")}
          className="edit-mode-only fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-black text-white text-[10px] uppercase tracking-widest font-bold px-4 h-10 items-center gap-2 shadow-2xl border border-white/20"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Ripristina {hidden.length} sezioni eliminate
        </button>
      )}
    </>
  );
}
